import { Plus, Receipt, Printer, XCircle, ChefHat, CupSoda, Clock } from "lucide-react";
import { mergeLines } from "../../lib/invoicePrint";
import { formatCurrency } from "../../lib/utils";

const statusLabel = { open: "Novo", preparing: "Na cozinha", ready: "Pronto", served: "Servido" };
const statusCls = {
  open: "bg-zinc-700 text-zinc-100",
  preparing: "bg-amber-500 text-black",
  ready: "bg-sky-500 text-white",
  served: "bg-violet-500 text-white",
};

const elapsed = (iso) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h ${m % 60}m`;
};

const btn = "h-8 rounded-md text-xs font-semibold flex items-center justify-center gap-1 transition-colors";

export const POSOrdersView = ({ openOrders, tables, onOpenOrder, onCloseTable, onPreBill, onCancel }) => (
  <div className="flex-1 min-h-0 overflow-y-auto p-3" data-testid="pos-orders-view">
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      {openOrders.map((o) => {
        const tbl = tables.find((t) => t.table_id === o.table_id);
        const label = tbl ? `Mesa ${tbl.number}` : o.order_type === "delivery" ? "Entrega" : "Balcão / Take-away";
        return (
          <div key={o.order_id} className="rounded-xl border border-red-500/40 bg-[#262626] p-3 flex flex-col gap-2" data-testid={`pos-order-card-${o.order_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-lg font-black font-mono leading-none text-zinc-100">#{o.order_number}</p>
                <p className="text-[11px] text-zinc-400 truncate">
                  {label}
                  {o.customer_name ? ` · ${o.customer_name}` : ""}
                  {o.waiter_name ? ` · ${o.waiter_name}` : ""}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <span className="px-2 py-0.5 rounded bg-red-600 text-white text-[10px] font-black tracking-wide" data-testid={`pos-order-unpaid-${o.order_id}`}>
                  {o.offline ? "OFFLINE · NÃO PAGA" : "NÃO PAGA"}
                </span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${statusCls[o.status] || statusCls.open}`}>{statusLabel[o.status] || o.status}</span>
              </div>
            </div>
            <ul className="text-[11px] text-zinc-300 space-y-0.5 max-h-24 overflow-y-auto">
              {mergeLines(o.items, { byNotes: true }).map((it, i) => (
                <li key={it.item_id || i} className="flex justify-between gap-2">
                  <span className="truncate">
                    {it.quantity}x {it.product_name}
                  </span>
                  <span className="font-mono text-zinc-400 shrink-0">{formatCurrency(it.line_total ?? it.unit_price * it.quantity)}</span>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between text-[11px] text-zinc-400">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" /> {elapsed(o.created_at)}
                {o.requires_kitchen === false ? <CupSoda className="w-3 h-3 ml-1" /> : <ChefHat className="w-3 h-3 ml-1" />}
                {o.cooking_time ? ` · ${o.cooking_time}` : ""}
              </span>
              <span className="font-mono font-bold text-base text-red-300">{formatCurrency(o.total)}</span>
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              <button onClick={() => onOpenOrder(o)} className={`${btn} bg-[#333] hover:bg-[#3d3d3d] text-zinc-100`} title="Adicionar itens" data-testid={`pos-order-add-${o.order_id}`}>
                <Plus className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => onPreBill(o)} className={`${btn} bg-sky-600 hover:bg-sky-500 text-white`} title="Consulta de mesa" data-testid={`pos-order-prebill-${o.order_id}`}>
                <Printer className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => onCancel(o)} className={`${btn} bg-[#333] hover:bg-red-600 text-red-300 hover:text-white`} title="Cancelar pedido" data-testid={`pos-order-cancel-${o.order_id}`}>
                <XCircle className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => onCloseTable(o)} className={`${btn} bg-red-600 hover:bg-red-500 text-white`} data-testid={`pos-order-pay-${o.order_id}`}>
                <Receipt className="w-3.5 h-3.5" /> Faturar
              </button>
            </div>
          </div>
        );
      })}
    </div>
    {openOrders.length === 0 && (
      <div className="flex flex-col items-center justify-center py-16 text-zinc-500">
        <Receipt className="w-10 h-10 mb-3 opacity-40" />
        <p className="text-sm">Sem pedidos em curso — todas as contas estão fechadas</p>
      </div>
    )}
  </div>
);
